import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, Plus, Shield, AlertTriangle } from 'lucide-react';
import PageHeader from '../common/PageHeader';
import PageBreadcrumb from '../common/PageBreadcrumb';
import { useToast } from '../common/Toast';
import complianceService from '../../services/complianceService';

const FrameworkForm = () => {
  const { id } = useParams(); // If ID exists, we are in Edit mode
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    type: 'Regulatory',
    requirements: [] 
  });

  useEffect(() => {
    if (id) {
      loadFramework(id);
    }
  }, [id]);

  const loadFramework = async (frameworkId) => {
    setLoading(true);
    try {
      const res = await complianceService.getFrameworks(); 
      const found = res.data.find(f => f._id === frameworkId || f.id === frameworkId);
      if (found) setFormData(found);
    } catch (err) {
      showError('Failed to load framework details');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (id) {
        // Update existing record
        await complianceService.updateFramework(id, formData);
        success('Framework updated successfully');
        navigate('/contracts/frameworks');
      } else {
        // Create new record
        const res = await complianceService.createFramework(formData);
        success('Framework created successfully');
        // Redirect to the Add Requirement page immediately after creation? 
        // Or back to list? Using list based on your previous flow preference.
        navigate('/contracts/frameworks');
      }
    } catch (err) {
      showError(err.message || 'Failed to save framework');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title={id ? "Edit Framework" : "Create Framework"} 
        description="Define the regulation and view its specific control requirements."
      />

      <div className="p-6 max-w-5xl mx-auto">
        <form onSubmit={handleSave}>
          
          {/* MASTER DETAILS CARD */}
          <div className="bg-white border rounded-lg shadow-sm p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Shield size={20} className="text-primary" /> 
              Framework Details
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="form-label">Framework Name *</label>
                <input 
                  type="text" className="form-control" required
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g. GDPR"
                />
              </div>
              <div>
                <label className="form-label">Framework Code *</label>
                <input 
                  type="text" className="form-control" required
                  value={formData.code}
                  onChange={e => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  placeholder="e.g. EU-GDPR"
                />
              </div>
              <div>
                <label className="form-label">Type</label>
                <select 
                  className="form-select"
                  value={formData.type}
                  onChange={e => setFormData({...formData, type: e.target.value})}
                >
                  <option>Regulatory</option>
                  <option>Industry Standard</option>
                  <option>Internal Policy</option>
                </select>
              </div>
              <div className="lg:col-span-3 space-y-2">
                <label className="form-label font-medium text-gray-700">Description</label>
                <textarea 
                  className="form-control" rows="4"
                  value={formData.description}
                  onChange={e => setFormData({...formData, description: e.target.value})}
                  placeholder="Provide a detailed overview of the framework objectives..."
                />
              </div>
            </div>
          </div>

          {/* REQUIREMENTS CARD - Only shown in Edit Mode */}
          {id && (
            <div className="bg-white border rounded-lg shadow-sm p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <AlertTriangle size={20} className="text-warning" />
                  Specific Requirements / Controls
                </h3>
                
                {/* NEW ADD BUTTON: Redirects to the dedicated page */}
                <button 
                  type="button" 
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => navigate(`/contracts/frameworks/${id}/requirements`)}
                >
                  <Plus size={16} className="me-2" />
                  Add New Requirement
                </button>
              </div>

              {/* Read-Only List (No Delete Button) */}
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead className="bg-light">
                    <tr>
                      <th width="20%">Code</th>
                      <th width="65%">Requirement Description</th>
                      <th width="15%">Risk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.requirements.length === 0 && (
                      <tr><td colSpan="3" className="text-center text-muted py-4">No requirements defined. Click "Add New Requirement".</td></tr>
                    )}
                    {formData.requirements.map((req, idx) => (
                      <tr key={idx}>
                        <td className="fw-bold">{req.code}</td>
                        <td>{req.description}</td>
                        <td>
                          <span className={`badge ${req.riskLevel === 'High' || req.riskLevel === 'Critical' ? 'bg-danger' : 'bg-secondary'}`}>
                            {req.riskLevel}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/contracts/frameworks')}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              <Save size={18} className="me-2" />
              {loading ? 'Saving...' : 'Save Framework'}
            </button>
          </div>

        </form>
      </div>
    </>
  );
};

export default FrameworkForm;